import { gql } from "@apollo/client";

import { Consultation, ConsultationRating } from "../models/consultation";
import { Consent, Member, MemberAlert } from "../models/member";
import { Message } from "../models/message";
import { Notification } from "../models/notification";
import { Order } from "../models/orders";
import { Promotion } from "../models/promotions";

export const PASSWORD_RECOVERY = gql`
  mutation PasswordRecovery($email: String!, $language: String!) {
    authMutations {
      passwordRecovery(email: $email, language: $language)
    }
  }
`;

export const PASSWORD_RESET = gql`
  mutation PasswordReset($code: String!, $password: String!) {
    authMutations {
      passwordReset(code: $code, password: $password)
    }
  }
`;

export const CREATE_MEMBER = gql`
  mutation CreateMember($input: MemberInput!, $consents: CreateMemberConsent!) {
    memberMutations {
      createMember(input: $input, consents: $consents) {
        memberUid
        profile {
          pseudo
          remainingTime
          memberCode
        }
        consents {
          purposeUid
          purposeKey
          type
          contentId
          required
          userUid
          value
        }
      }
    }
  }
`;

export const UPDATE_MEMBER = gql`
  mutation UpdateMember($input: MemberInput!) {
    memberMutations {
      updateMember(input: $input) {
        firstname
        lastname
        birthday
        email
        address
        complement
        zip
        city
        country
        region
        lang
        queueNotificationChannels
      }
    }
  }
`;

export const UPDATE_PSEUDO = gql`
  mutation UpdatePseudo($pseudo: String!) {
    memberMutations {
      updatePseudo(pseudo: $pseudo) {
        pseudo
        memberCode
        remainingTime
        weeklyOrdersLimit
        favorites
        fidelityPoints
      }
    }
  }
`;

export const CLOSE_ACCOUNT = gql`
  mutation CloseAccount {
    memberMutations {
      closeAccount
    }
  }
`;

export const CHANGE_WEEKLY_ORDERS_LIMIT = gql`
  mutation ChangeWeeklyOrdersLimit($weeklyOrdersLimit: Int!) {
    memberMutations {
      changeWeeklyOrdersLimit(weeklyOrdersLimit: $weeklyOrdersLimit)
    }
  }
`;

export const BLOCK_PAYMENT = gql`
mutation PaymentBlocking($blockingRef: String!, $blockingType: String!, $metadata: String) {
  paymentMutations {
    paymentBlocking(blockingRef: $blockingRef, blockingType: $blockingType, metadata: $metadata)
  }
}
`;

export const UNBLOCK_PAYMENT = gql`
mutation PaymentUnblocking($paymentBlockingUid: String!) {
  paymentMutations {
    paymentUnblocking(paymentBlockingUid: $paymentBlockingUid)
  }
}
`;

export const CHANGE_CONSENT = gql`
  mutation ChangeConsent($input: ConsentInput!) {
    memberMutations {
      changeConsent(input: $input) {
        purposeUid
        purposeKey
        type
        contentId
        required
        value
      }
    }
  }
`;

export const CHANGE_CONSENTS = gql`
  mutation ChangeConsents($input: [ConsentInput]!) {
    memberMutations {
      changeConsents(input: $input) {
        purposeUid
        purposeKey
        type
        contentId
        required
        value
      }
    }
  }
`;

export const VALIDATE_PROMOCODE = gql`
  mutation ValidateCode($code: String!, $codes: [String]!, $packUid: String!) {
    promotionMutations {
      validatePromoCode(code: $code, codes: $codes, packUid: $packUid) {
        promoCodeUid
        code
        type
        value
        cumulative
      }
    }
  }
`;

export const WELCOME_OFFER_ELIGIBILITY = gql`
  mutation WelcomeOfferEligibility {
    promotionMutations {
      welcomeOfferEligibility
    }
  }    
`;

export const WELCOME_OFFER_REDEEM = gql`
  mutation WelcomeOfferRedeem {
    promotionMutations {
      welcomeOfferRedeem {
        error
        success
      }
    }
  }
`;

export const CREATE_ORDER = gql`
  mutation CreateOrder($input: CreateOrderInput!, $validateOrdersLimit: Boolean) {
    ordersMutations {
      createOrder(input: $input, validateOrdersLimit: $validateOrdersLimit) {
        orderId
        paymentLink
      }
    }
  }
`;

export const CREATE_WRITTEN_CONSULTATION = gql`
  mutation CreateConsultationWritten($input: ConsultationWrittenInput!) {
    consultationMutations {
      createConsultationWritten(input: $input) {
        consultationUid
        duration
      }
    }
  }
`;

export const PURGE_CONSULTATION = gql`
  mutation PurgeConsultation ($consultationUid: String!) {
    consultationMutations {
      purgeConsultation(consultationUid: $consultationUid)
    }
  }
`;

export const ADD_CONSULTATION_RATING = gql`
  mutation AddConsultationRating($input: ConsultationRatingInput!) {
    consultationMutations {
      addConsultationRating(input: $input) {
        ratingUid
        rate
        comment
        ratingAnswer {
          ratingAnswerUid
          ratingUid
          expertUid
          comment
          validationComment
          language
          status
          validationRules
          context
          createdAt
          updatedAt
          publishAt
        }
        refused
        moderation
        validationComment
      }
    }
  }
`;

export const UPDATE_CONSULTATION_RATING = gql`
  mutation UpdateConsultationRating($ratingUid: String!, $input: ConsultationRatingInput!) {
    consultationMutations {
      updateConsultationRating(ratingUid: $ratingUid, input: $input) {
        ratingUid
        rate
        comment
        ratingAnswer {
          ratingAnswerUid
          ratingUid
          expertUid
          comment
          validationComment
          language
          status
          validationRules
          context
          createdAt
          updatedAt
          publishAt
        }
        createdAt
        refused
        validationComment
      }
    }
  }
`;

export const TRIAL_OFFER_ACTION = gql`
  mutation TrialOfferAction($trialOfferUid: String!, $action: TrialOfferAction!) {
    consultationMutations {
      trialOfferAction(trialOfferUid: $trialOfferUid, action: $action)
    }
  }
`;

export const TRIAL_CONSULTATION_FEEDBACK = gql`
  mutation TrialConsultationFeedback($trialUid: String!, $trialConsultationUid: String!, $feedback: TrialConsultationFeedback) {
    consultationMutations {
      trialConsultationFeedback(trialUid: $trialUid, trialConsultationUid: $trialConsultationUid, feedback: $feedback)
    }
  }
`;

export const STOP_LIVECHAT_CONSULTATION = gql`
  mutation StopLivechatConsultation($livechatRoomUid: String!) {
    consultationMutations {
      stopLivechatConsultation(livechatRoomUid: $livechatRoomUid)
    }
  }
`;

export const STOP_VIDEO_CONSULTATION = gql`
  mutation StopVideoConsultation($videoRoomUid: String!) {
    consultationMutations {
      stopVideoConsultation(videoRoomUid: $videoRoomUid)
    }
  }
`;

export const ADD_MEMBER_FAVORITE = gql`
  mutation AddFavorite($favorite: ID!) {
    memberMutations {
      addFavorite(favorite: $favorite)
    }
  }
`;

export const REMOVE_MEMBER_FAVORITE = gql`
  mutation RemoveFavorite($favorite: ID!) {
    memberMutations {
      removeFavorite(favorite: $favorite)
    }
  }
`;

export const ADD_MEMBER_ALERT = gql`
  mutation AddAlert($input: AddAlertInput!) {
    memberMutations {
      addAlert(input: $input) {
        alertUid
        expertUid
        expertChannels
        notificationChannels
        validTo
      }
    }
  }
`;

export const EDIT_MEMBER_ALERT = gql`
  mutation EditAlert($input: EditAlertInput!) {
    memberMutations {
      editAlert(input: $input) {
        alertUid
        expertUid
        expertChannels
        notificationChannels
        validTo
      }
    }
  }
`;

export const REMOVE_MEMBER_ALERT = gql`
  mutation RemoveAlert($alertUid: ID!) {
    memberMutations {
      removeAlert(alertUid: $alertUid)
    }
  }
`;

export const REFRESH_MEMBER_PHONE_NUMBER_CODE = gql`
  mutation RefreshPhoneNumberCode($phoneNumber: String!, $language: String!, $recaptcha: String!) {
    memberMutations {
      refreshPhoneNumberCode(phoneNumber: $phoneNumber, language: $language, recaptcha: $recaptcha) {
        phoneCode
        type
      }
    }
  }
`;

export const VALIDATE_MEMBER_PHONE_NUMBER = gql`
  mutation ValidatePhoneNumber($input: PhoneNumberValidationInput!) {
    memberMutations {
      validatePhoneNumber(input: $input) {
        phoneUid
        phoneNumber
        country
        carrier
        type
        status
        phoneCode
        createdAt
      }
    }
  }
`;

export const VALIDATE_MEMBER_EMAIL = gql`
  mutation ValidateEmail($code: String!) {
    memberMutations {
      validateEmail(code: $code)
    }
  }
`;

export const CHANGE_MEMBER_EMAIL = gql`
  mutation ChangeEmail($email: String!) {
    memberMutations {
      changeEmail(email: $email)
    }
  }
`;

export const SET_MEMBER_PHONE_NUMBER_USAGE = gql`
  mutation SetPhoneNumberUsage($phoneUid: ID, $usage: String!) {
    memberMutations {
      setPhoneNumberUsage(phoneUid: $phoneUid, usage: $usage)
    }
  }
`;

export const REMOVE_MEMBER_PHONE_NUMBER = gql`
  mutation RemovePhoneNumber($phoneUid: ID!) {
    memberMutations {
      removePhoneNumber(phoneUid: $phoneUid)
    }
  }
`;

export const CREATE_NOTIFICATION_ACK = gql`
  mutation CreateNotificationAck($notificationUid: String!) {
    notificationMutations {
      createNotificationAck(notificationUid: $notificationUid) {
        notificationUid
      }
    }
  }
`;

export const CREATE_NOTIFICATION_PUSH_SUBSCRIPTION = gql`
  mutation CreateNotificationPushSubscription($subscription: String!) {
    notificationMutations {
      createNotificationPushSubscription(subscription: $subscription)
    }
  }
`;

export const ACK_ALL_NOTIFICATIONS = gql`
  mutation AckAllNotifications {
    notificationMutations {
      ackAllNotifications
    }
  }
`;

export const DELETE_MESSAGE = gql`
  mutation DeleteMessage($messageUid: String!) {
    messagingMutations {
      deleteMessage(messageUid: $messageUid)
    }
  }
`;

export const CREATE_MESSAGES_VIEWS = gql`
  mutation CreateMessagesViews($messages: [String!]!) {
    messagingMutations {
      createMessagesViews(messages: $messages)
    }
  }
`;

export const CREATE_MESSAGE = gql`
  mutation CreateMessage($recipient: String!, $content: String!, $language: String!, $attachments: [String!]) {
    messagingMutations {
      createMessage(recipient: $recipient, content: $content, language: $language, attachments: $attachments) {
        messageUid
      }
    }
  }
`;

export const CREATE_BLOCKING = gql`
  mutation CreateBlocking($action: String!, $sender: String!) {
    messagingMutations {
      createBlocking(action: $action, sender: $sender)
    }
  }
`;

export const SUBSCRIBE_HOROSCOPE_NEWSLETTER = gql`
  mutation SubscribeHoroscope($subscription: HoroscopeSubscription!) {
    newslettersMutations {
      subscribeHoroscope(subscription: $subscription)
    }
  }
`;

export const STOP_CALL = gql`
  mutation StopCall($callLegUid: String, $ivrCallUid: String) {
    twilioMutations {
      stopCall(callLegUid: $callLegUid, ivrCallUid: $ivrCallUid)
    }
  }
`;

export const JOIN_QUEUE = gql`
mutation JoinQueue(
  $queueUid: String!, $type: QueueType!, $expertUid: String!, $phoneUid: String, $language: String!, 
  $memberPseudo: String, $dropAt: String
) {
  queueMutations {
    joinQueue(
      queueUid: $queueUid, type: $type, expertUid: $expertUid, phoneUid: $phoneUid, language: $language, 
      memberPseudo: $memberPseudo, dropAt: $dropAt
    ) {
      success, queueItemUid, memberPhoneUid, servicePhone,
    }
  }
}
`;

export const DROP_QUEUE = gql`
mutation DropQueue($queueUid: String!, $queueItemUid: String!) {
  queueMutations {
    dropQueue(queueUid: $queueUid, queueItemUid: $queueItemUid) {
      success
    }
  }
}
`;

export const EDIT_QUEUE_ITEM = gql`
mutation EditQueueItem($queueUid: String!, $queueItemUid: String!, $update: QueueItemUpdate!) {
  queueMutations {
    editQueueItem(queueUid: $queueUid, queueItemUid: $queueItemUid, update: $update)
  }
}
`;

export const CREATE_TICKET = gql`
mutation CreateTicket($createTicketInput: CreateTicketInput!) {
  supportMutations {
    createTicket(createTicketInput: $createTicketInput) {
      ticketUid
    }
  }
}
`;

export const ADD_TICKET_ANSWER = gql`
mutation AddTicketAnswer($ticketAnswer: TicketAnswerInput!, $ticketUid: String!) {
  supportMutations {
    addTicketAnswer(ticketAnswer: $ticketAnswer, ticketUid: $ticketUid)
  }
}
`;

export const ACKNOWLEDGE_ADVISORY = gql`
mutation AcknowledgeAdvisory($advisoryUid: String!) {
  advisoryMutations {
    acknowledgeAdvisory(advisoryUid: $advisoryUid)
  }
}
`;

export const ACK_ANNOUNCEMENTS = gql`
mutation AckAnnouncements($announcementUids: [String]!) {
  announcementMutations {
    ackAnnouncements(announcementUids: $announcementUids)
  }
}
`;

export const RESPOND_TO_SURVEY = gql`
mutation RespondToSurvey($questionUid: ID!, $response: [String]!, $responseUid: ID!) {
  surveyMutations {
    createResponse(questionUid: $questionUid, response: $response, responseUid: $responseUid) {
      questionResponseUid
    }
  }
}
`;

export const SEND_EVENT_LOG = gql`
mutation SendEventLog($subject: String!, $event: String!, $level: String!, $ts: String!, $metadata: String) {
  logsMutations {
    sendEventLog(subject: $subject, event: $event, level: $level, ts: $ts, metadata: $metadata) 
  }
}
`;

export interface AuthMutations {
  authMutations: {
    passwordRecovery: boolean
    passwordReset: boolean
  }
}

export interface SurveyMutations {
  surveyMutations: {
    createResponse: void;
  }
}

export interface MemberMutations {
  memberMutations: {
    createMember?: Member
    updateMember?: Member
    changeConsent?: Consent
    addFavorite?: string
    removeFavorite?: string
    addAlert?: MemberAlert
    editAlert?: MemberAlert
    removeAlert?: boolean
  }
}

export interface PromotionMutations {
  promotionMutations: {
    validateCode: Promotion
    welcomeOfferEligibilty: boolean;
    welcomeOfferRedeem: {
      success: boolean
      error?: string
    }
  }
}

export interface OrdersMutations {
  ordersMutations: {
    createOrder: Order
  }
}

export interface ConsultationMutations {
  consultationMutations: {
    createConsultationWritten: Consultation
    createConsultationLivechat: Consultation
    createConsultationPhone: Consultation
    addConsultationRating: ConsultationRating
    updateConsultationRating: ConsultationRating
    // createQueueItem: QueueItem
    trialOfferAction: boolean;
    trialConsultationFeedback: boolean;
    stopLivechatConsultation: boolean;
    stopVideoConsultation: boolean;
  }
}

export interface NotificationMutations {
  notificationMutations: {
    createNotificationAck: Notification;
    createPushNotificationSubscription: boolean;
  }
}

export interface MessagingMutations {
  messagingMutations: {
    deleteMessage: boolean;
    createMessagesViews: boolean;
    createMessage: Message;
    createBlocking: boolean;
  }
}

export interface TwilioMutations {
  twilioMutations: {
    stopCall: boolean;
  }
}

export interface QueueActionResult {
  success: boolean;
  queueItemUid?: string;
  memberPhoneUid?: string;
  servicePhone?: string;
}

export interface QueueMutations {
  queueMutations: {
    joinQueue: QueueActionResult;
    dropQueue: QueueActionResult;
    editQueueItem: boolean;
  }
}

export interface AnnouncementMutations {
  announcementMutations: {
    ackAnnouncements: boolean;
  };
}
